import {
  Button,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { Item, ItemAssignment, WithId } from "types";
import { createItem, getEntityList } from "helpers/utils";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import AutoSuggestUserInput from "./AutoSuggestUserInput";
import { first } from "lodash";

interface ItemAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: WithId<Item>;
  onAssign?: (assignment: any) => void;
}

export default function ItemAssignmentModal({
  isOpen,
  onClose,
  item,
  onAssign,
}: ItemAssignmentModalProps) {
  const [assignments, setAssignments] = useState<WithId<ItemAssignment>[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>();
  const toast = useToast();

  useEffect(() => {
    if (isOpen && item.id) {
      // Get assignment history
      getEntityList({
        entity: "ItemAssignment",
        fields: ["*", "assigned_to.*", "user_created.*"],
        filter: { item_id: { _eq: item.id } },
        sort: ["-date_created"],
        paginate: false,
      }).then((result) => {
        if (result?.list) {
          setAssignments(result.list as WithId<ItemAssignment>[]);
          // Set current assignment if exists
          if (result.list[0]) {
            setSelectedUser(result.list[0].assigned_to.id);
          }
        }
      });
    }
  }, [isOpen, item.id]);

  const handleAssign = async () => {
    if (!selectedUser) return;

    try {
      const assignment = await createItem(
        "ItemAssignment",
        {
          item_id: item.id,
          assigned_to: selectedUser,
        },
        { fields: ["*", "assigned_to.*", "user_created.*"] }
      );
      toast({
        title: "Success",
        description: "Item assigned successfully",
        status: "success",
      });
      onAssign?.(assignment);
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message ?? "Failed to assign item",
        status: "error",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"xl"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Assign {item.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <AutoSuggestUserInput
              value={selectedUser}
              onChange={(val: any) => setSelectedUser(val?.value)}
              isClearable
            />

            <Divider />

            <Stack spacing={2}>
              <Text fontWeight="bold">Assignment History</Text>
              {assignments.map((assignment) => (
                <Stack
                  key={assignment.id}
                  direction="row"
                  justify="space-between"
                  fontSize="sm"
                >
                  <Text>
                    {assignment.assigned_to.first_name}{" "}
                    {assignment.assigned_to.last_name} -{" "}
                    {assignment.assigned_to.email}
                  </Text>
                  <Text color="gray.500">
                    {format(new Date(assignment.date_created), "MMM dd, yyyy")}
                  </Text>
                </Stack>
              ))}
            </Stack>
            <Divider />
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Close
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleAssign}
            isDisabled={
              !selectedUser ||
              selectedUser === first(assignments)?.assigned_to.id
            }
          >
            Assign
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
